import type { Artist, AuctionType } from '../types/game'

// ===================
// ARTISTS (Board Order - Left to Right)
// ===================
// This order is CRITICAL for tie-breaking in artist rankings
// Priority 1 (highest) -> Priority 5 (lowest)

export const ARTISTS: readonly Artist[] = [
  'Solstice Vane', // Priority 1 (wins ties)
  'Mira Faux', // Priority 2
  'Atlas Zero', // Priority 3
  'Vesper Kint', // Priority 4
  'Luxe Nebula', // Priority 5 (loses ties)
] as const

// Artist display colors for UI
export const ARTIST_COLORS = [
  { name: 'Solstice Vane', color: '#F5C846', textColor: '#000000' },
  { name: 'Mira Faux', color: '#2DD4BF', textColor: '#000000' },
  { name: 'Atlas Zero', color: '#DC2626', textColor: '#FFFFFF' },
  { name: 'Vesper Kint', color: '#22C55E', textColor: '#000000' },
  { name: 'Luxe Nebula', color: '#A855F7', textColor: '#FFFFFF' },
] as const

export const AUCTION_TYPES: readonly AuctionType[] = [
  'open',
  'one_offer',
  'hidden',
  'fixed_price',
  'double',
] as const

// ===================
// AUCTION TYPE DISPLAY NAMES
// ===================
// User-facing names for auction types (Bougie Art theme)

export const AUCTION_TYPE_NAMES: Record<AuctionType, { name: string; icon: string; shortName?: string; description: string }> = {
  open: { name: 'Flex', icon: '💪', description: 'Players take turns bidding. Highest bid wins.' },
  one_offer: { name: 'Inside Job', icon: '🤝', shortName: 'Inside Job', description: 'Each player gets one chance to bid.' },
  hidden: { name: 'Underground', icon: '🙈', description: 'All players submit bids secretly.' },
  fixed_price: { name: 'Price Fixing', icon: '💰', description: 'Auctioneer sets a price. First taker gets it.' },
  double: { name: 'Twofer', icon: '👯', description: 'Can be combined with another card.' },
} as const

// ===================
// CARD DISTRIBUTION
// ===================
// Total: 70 cards
// Easy to modify - just change the numbers and everything else updates

export const CARD_DISTRIBUTION: Record<Artist, number> = {
  'Solstice Vane': 12,
  'Mira Faux': 13,
  'Atlas Zero': 14,
  'Vesper Kint': 15,
  'Luxe Nebula': 16,
}

// ===================
// AUCTION TYPE DISTRIBUTION
// ===================
// Configurable distribution of auction types per artist
// DOUBLE cards: Minimum 2 per artist (can be increased)
// Other types: Remaining cards split roughly equally

export type AuctionTypeDistribution = {
  double: number // Minimum 2 per artist
  open?: number
  one_offer?: number
  hidden?: number
  fixed_price?: number
}

// Per-artist auction type distribution (easily configurable)
// Totals: Open=17, Fixed=11, Double=10, Hidden=14, OneOffer=18 (Total=70)
export const AUCTION_DISTRIBUTION: Record<Artist, AuctionTypeDistribution> = {
  'Solstice Vane': {
    double: 2,
    open: 3,
    one_offer: 3,
    hidden: 2,
    fixed_price: 2,
  },
  'Mira Faux': {
    double: 2,
    open: 3,
    one_offer: 3,
    hidden: 3,
    fixed_price: 2,
  },
  'Atlas Zero': {
    double: 2,
    open: 3,
    one_offer: 4,
    hidden: 3,
    fixed_price: 2,
  },
  'Vesper Kint': {
    double: 2,
    open: 4,
    one_offer: 4,
    hidden: 3,
    fixed_price: 2,
  },
  'Luxe Nebula': {
    double: 2,
    open: 4,
    one_offer: 4,
    hidden: 3,
    fixed_price: 3,
  },
}

export const CARDS_PER_ROUND: Record<number, [number, number, number, number]> = {
  3: [10, 6, 6, 0], // 3 players: 10, 6, 6, 0 cards per round
  4: [9, 4, 4, 0], // 4 players: 9, 4, 4, 0 cards per round
  5: [8, 3, 3, 0], // 5 players: 8, 3, 3, 0 cards per round
}

export const STARTING_MONEY = 100

export const ARTIST_VALUES = {
  FIRST: 30,
  SECOND: 20,
  THIRD: 10,
  FOURTH_OR_LOWER: 0,
} as const

// ===================
// PLAYER COLORS
// ===================

export const PLAYER_COLORS = [
  '#ef4444', // Red
  '#3b82f6', // Blue
  '#10b981', // Green
  '#f59e0b', // Yellow
  '#8b5cf6', // Purple
] as const

// ===================
// HELPER FUNCTIONS
// ===================

// Get player to the left (clockwise) of given player
export function getPlayerToLeft(playerIndex: number, playerCount: number): number {
  return (playerIndex + 1) % playerCount
}

// Get clockwise distance from player A to player B
export function clockwiseDistance(
  from: number,
  to: number,
  playerCount: number
): number {
  if (to >= from) {
    return to - from
  }
  return playerCount - from + to
}

// Get turn order starting left of auctioneer, ending with auctioneer
export function getTurnOrder(auctioneerIndex: number, playerCount: number): number[] {
  const order: number[] = []
  for (let i = 1; i <= playerCount; i++) {
    order.push((auctioneerIndex + i) % playerCount)
  }
  return order // Auctioneer is last
}

// ===================
// ARTIST HELPERS
// ===================

// Artist folder names for artwork paths
const ARTIST_FOLDERS = [
  'solstice_vane',
  'mira_faux',
  'atlas_zero',
  'vesper_kint',
  'luxe_nebula',
] as const

// Artist initials for card numbering (e.g., S-01, M-02, A-03, V-04, L-05)
const ARTIST_INITIALS = ['SV', 'MF', 'AZ', 'VK', 'LN'] as const

// Get artist color info by index
export function getArtistColor(artistIndex: number) {
  return ARTIST_COLORS[artistIndex] || ARTIST_COLORS[0]
}

// Get artist folder name for artwork path
export function getArtistFolderName(artistIndex: number): string {
  return ARTIST_FOLDERS[artistIndex] || ARTIST_FOLDERS[0]
}

// Get artist initials for card numbering
export function getArtistInitials(artistIndex: number): string {
  return ARTIST_INITIALS[artistIndex] || 'X'
}

// Get artist index from artist name
export function getArtistIndex(artistName: Artist): number {
  return ARTISTS.indexOf(artistName)
}
